import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { useDirectoryBundle } from "../api/directory";
import {
  CohortFilterBar,
  type CohortFilterValue,
} from "../components/cohorts/CohortFilterBar";
import {
  RichLessonReport,
  reportLessonRating,
  useLessonReportList,
} from "../api/lessonReports";
import type { ManeuverCode } from "../constants/lessonReportCatalog";
import { MANEUVER_ITEMS, MANEUVER_TABLE_HEADERS } from "../constants/lessonReportCatalog";
import LessonReportModal from "../components/lesson-reports/LessonReportModal";
import LessonReportStarDisplay from "../components/lesson-reports/LessonReportStarDisplay";
import LessonReportNoteCell from "../components/lesson-reports/LessonReportNoteCell";
import { SortableTableHeader } from "../components/common/SortableTableHeader";
import { BranchFilterSelect } from "../components/common/BranchFilterSelect";
import { usePlatformBranchFilter } from "../hooks/usePlatformBranchFilter";
import { formatPlateDisplay } from "../utils/plate";
import type { LessonReportSortDir, LessonReportSortField } from "../api/lessonReports";
import { UI_LOADING } from "../constants/uiLabels";
import { ListPaginationBar, useListPageSize, usePagedOffset } from "../components/ListPagination";
import { useRegisterListPageStats } from "../context/ListPageStatsContext";
import "./LessonReportsPage.css";

const META_COLS = 7;
const EMPTY_COHORT: CohortFilterValue = { periodId: "", groupId: "", subgroupId: "" };

function fmtDateOnly(iso: string | null | undefined) {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString("tr-TR");
}

function fmtTimeRange(start: string | null | undefined, end: string | null | undefined) {
  if (!start) return "—";
  const s = new Date(start);
  if (Number.isNaN(s.getTime())) return "—";
  const startStr = s.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
  if (!end) return startStr;
  const e = new Date(end);
  if (Number.isNaN(e.getTime())) return startStr;
  const endStr = e.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
  return `${startStr} – ${endStr}`;
}

function maneuverCount(
  counts: Partial<Record<ManeuverCode, number>> | undefined,
  code: ManeuverCode,
): number {
  return counts?.[code] ?? 0;
}

export default function LessonReportsPage() {
  const { session } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const role = session?.user?.role;
  const canSelectInstructor =
    role === "tenant_admin" || role === "manager" || role === "office_staff";
  const { isPlatformOwner, branchFilter, setBranchFilter, branches } = usePlatformBranchFilter();
  const [cohortFilter, setCohortFilter] = useState<CohortFilterValue>(EMPTY_COHORT);
  const directoryBranchId = isPlatformOwner ? branchFilter || undefined : undefined;
  const directory = useDirectoryBundle(directoryBranchId, {
    trainingPeriodId: cohortFilter.periodId || undefined,
    trainingGroupId: cohortFilter.groupId || undefined,
    trainingSubgroupId: cohortFilter.subgroupId || undefined,
  });

  const [searchInput, setSearchInput] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [instructorId, setInstructorId] = useState("");
  const [studentId, setStudentId] = useState(() => searchParams.get("student") ?? "");
  const [attended, setAttended] = useState<"" | "true" | "false">("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [sortBy, setSortBy] = useState<LessonReportSortField>("starts_at");
  const [sortDir, setSortDir] = useState<LessonReportSortDir>("desc");
  const [opening, setOpening] = useState<RichLessonReport | null>(null);

  const handleSort = (column: LessonReportSortField) => {
    if (sortBy === column) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      return;
    }
    setSortBy(column);
    setSortDir(column === "starts_at" ? "desc" : "asc");
  };

  useEffect(() => {
    const fromUrl = searchParams.get("student") ?? "";
    setStudentId(fromUrl);
  }, [searchParams]);

  useEffect(() => {
    if (!studentId || !directory.data) return;
    if (!directory.data.students.some((s) => s.id === studentId)) {
      setStudentId("");
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        params.delete("student");
        return params;
      });
    }
  }, [cohortFilter, directory.data, studentId, setSearchParams]);

  useEffect(() => {
    const handle = setTimeout(() => {
      setAppliedSearch(searchInput.trim());
    }, 300);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const filteredStudentName = studentId
    ? directory.data?.students.find((s) => s.id === studentId)?.full_name
    : null;

  const pageSize = useListPageSize();
  const { offset, setOffset } = usePagedOffset(
    JSON.stringify([
      branchFilter,
      instructorId,
      studentId,
      attended,
      startDate,
      endDate,
      appliedSearch,
      sortBy,
      sortDir,
      pageSize,
    ]),
  );

  const { data, loading, error, reload } = useLessonReportList({
    branchId: branchFilter || undefined,
    instructorId: instructorId || undefined,
    studentId: studentId || undefined,
    attended: attended === "" ? "" : attended === "true",
    startDate: startDate || undefined,
    endDate: endDate || undefined,
    q: appliedSearch || undefined,
    sort: sortBy,
    order: sortDir,
    limit: pageSize,
    offset,
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  useRegisterListPageStats({
    label: "ders raporu",
    visible: items.length,
    total,
    loading,
  });

  const studentLookup = useMemo(() => {
    const map = new Map<string, string>();
    directory.data?.students.forEach((s) => map.set(s.id, s.full_name));
    return map;
  }, [directory.data]);

  const instructorLookup = useMemo(() => {
    const map = new Map<string, string>();
    directory.data?.instructors.forEach((i) => map.set(i.id, i.full_name));
    return map;
  }, [directory.data]);

  const pageTotals = useMemo(() => {
    const maneuverSums = Object.fromEntries(
      MANEUVER_ITEMS.map((m) => [m.code, 0]),
    ) as Record<ManeuverCode, number>;

    const scores: number[] = [];

    for (const r of items) {
      if (!r.attended) continue;
      for (const m of MANEUVER_ITEMS) {
        maneuverSums[m.code] += maneuverCount(r.maneuver_counts, m.code);
      }
      const score = reportLessonRating(r);
      if (score !== null) scores.push(score);
    }

    const avgScore =
      scores.length > 0
        ? Math.round((scores.reduce((a, b) => a + b, 0) / scores.length) * 10) / 10
        : null;

    return { maneuverSums, avgScore, scoredLessons: scores.length };
  }, [items]);

  return (
    <div className="lr-page">
      <header className="lr-page__header">
        <div>
          <h2>Ders Detay Raporları</h2>
          <p className="lr-page__sub">
            Eğitmenlerin Tamamladığı Ders Raporları. Her Rapor Randevuyu Otomatik Olarak
            &quot;Tamamlandı&quot; Durumuna Alır Ve Araç Kilometresini İleri Yönlü Günceller.
          </p>
        </div>
        <button type="button" className="kp-btn" onClick={reload} disabled={loading}>
          Yenile
        </button>
      </header>

      {studentId && (
        <div className="lr-page__student-banner" role="status">
          <span>
            <strong>Kursiyer filtresi:</strong>{" "}
            {filteredStudentName || studentLookup.get(studentId) || studentId}
          </span>
          <button
            type="button"
            className="kp-btn kp-btn--small"
            onClick={() => {
              setStudentId("");
              setSearchParams((prev) => {
                const params = new URLSearchParams(prev);
                params.delete("student");
                return params;
              });
            }}
          >
            Filtreyi kaldır
          </button>
        </div>
      )}

      <section className="lr-filters" aria-label="Filtreler">
        <CohortFilterBar
          value={cohortFilter}
          onChange={(v) => {
            setCohortFilter(v);
          }}
          className="lr-filters__cohort"
        />
        <input
          className="kp-input lr-filters__search"
          placeholder="Ad, plaka veya not ara…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        {canSelectInstructor && (
          <select
            className="kp-input"
            value={instructorId}
            onChange={(e) => {
              setInstructorId(e.target.value);
            }}
          >
            <option value="">Tüm Eğitmenler</option>
            {directory.data?.instructors.map((i) => (
              <option key={i.id} value={i.id}>
                {i.full_name}
              </option>
            ))}
          </select>
        )}
        <select
          className="kp-input"
          value={studentId}
          onChange={(e) => {
            const next = e.target.value;
            setStudentId(next);
            setSearchParams((prev) => {
              const params = new URLSearchParams(prev);
              if (next) params.set("student", next);
              else params.delete("student");
              return params;
            });
          }}
        >
          <option value="">Tüm Kursiyerler</option>
          {directory.data?.students.map((s) => (
            <option key={s.id} value={s.id}>
              {s.full_name}
            </option>
          ))}
        </select>
        <select
          className="kp-input"
          value={attended}
          onChange={(e) => {
            setAttended(e.target.value as "" | "true" | "false");
          }}
        >
          <option value="">Tüm Durumlar</option>
          <option value="true">Geldi</option>
          <option value="false">Gelmedi</option>
        </select>
        <input
          type="date"
          className="kp-input"
          aria-label="Başlangıç tarihi"
          value={startDate}
          onChange={(e) => {
            setStartDate(e.target.value);
          }}
        />
        <input
          type="date"
          className="kp-input"
          aria-label="Bitiş tarihi"
          value={endDate}
          onChange={(e) => {
            setEndDate(e.target.value);
          }}
        />
        {isPlatformOwner && (
          <BranchFilterSelect
            value={branchFilter}
            onChange={setBranchFilter}
            branches={branches}
          />
        )}
      </section>

      {error && <div className="lr-page__error">{error}</div>}

      <section className="lr-table-wrap">
        {loading && <div className="lr-page__hint">{UI_LOADING}</div>}
        {!loading && items.length === 0 && (
          <div className="lr-page__hint">Filtreye uyan rapor bulunamadı.</div>
        )}
        {!loading && items.length > 0 && (
          <table className="lr-table lr-table--matrix kp-data-table">
            <thead>
              <tr>
                <th className="lr-col-idx kp-th-meta" scope="col">
                  #
                </th>
                <SortableTableHeader
                  label="Tarih"
                  column="starts_at"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={handleSort}
                  thClassName="lr-col-date kp-th-meta"
                />
                <th className="lr-col-time kp-th-meta" scope="col">
                  Saat
                </th>
                <SortableTableHeader
                  label="Kursiyer"
                  column="student_name"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={handleSort}
                  thClassName="lr-col-student kp-th-meta"
                />
                <SortableTableHeader
                  label="Eğitmen"
                  column="instructor_name"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={handleSort}
                  thClassName="lr-col-instructor kp-th-meta"
                />
                <SortableTableHeader
                  label="Araç"
                  column="vehicle_plate"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={handleSort}
                  thClassName="lr-col-plate kp-th-meta"
                />
                <SortableTableHeader
                  label="Durum"
                  column="attended"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={handleSort}
                  thClassName="lr-col-status kp-th-meta"
                />
                {MANEUVER_ITEMS.map((m) => {
                  const h = MANEUVER_TABLE_HEADERS[m.code];
                  const maneuverSort = `maneuver_${m.code}` as LessonReportSortField;
                  return (
                    <SortableTableHeader
                      key={m.code}
                      column={maneuverSort}
                      sortBy={sortBy}
                      sortDir={sortDir}
                      onSort={handleSort}
                      thClassName="lr-col-maneuver"
                    >
                      <span className="lr-maneuver-th" title={m.label}>
                        <span className="lr-maneuver-th__line">{h.line1}</span>
                        <span className="lr-maneuver-th__line">{h.line2}</span>
                      </span>
                    </SortableTableHeader>
                  );
                })}
                <SortableTableHeader
                  label="Ortalama"
                  column="lesson_rating_avg"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={handleSort}
                  thClassName="lr-col-rating kp-th-meta"
                />
                <SortableTableHeader
                  column="general_notes"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={handleSort}
                  thClassName="lr-col-note kp-th-meta"
                >
                  <span className="lr-maneuver-th">
                    <span className="lr-maneuver-th__line">Ders</span>
                    <span className="lr-maneuver-th__line">Notu</span>
                  </span>
                </SortableTableHeader>
                <th className="lr-col-action kp-th-meta" scope="col">
                  İşlem
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((r, index) => {
                const score = reportLessonRating(r);
                const counts = r.maneuver_counts || {};
                const studentName =
                  r.student_name || studentLookup.get(r.student_id) || "—";
                const instructorName =
                  r.instructor_name || instructorLookup.get(r.instructor_id) || "—";

                return (
                  <tr key={r.id}>
                    <td className="lr-col-idx lr-cell-num kp-tnum">{index + 1}</td>
                    <td className="lr-col-date kp-tnum">{fmtDateOnly(r.appointment.starts_at)}</td>
                    <td className="lr-col-time kp-tnum">
                      {fmtTimeRange(r.appointment.starts_at, r.appointment.ends_at)}
                    </td>
                    <td className="lr-col-student">{studentName}</td>
                    <td className="lr-col-instructor">{instructorName}</td>
                    <td className="lr-col-plate kp-tnum">
                      {r.vehicle_plate ? formatPlateDisplay(r.vehicle_plate) : "—"}
                    </td>
                    <td className="lr-col-status">
                      {r.attended ? (
                        <span className="badge badge-ok">Geldi</span>
                      ) : (
                        <span className="badge badge-warn">Gelmedi</span>
                      )}
                    </td>
                    {MANEUVER_ITEMS.map((m) => {
                      const n = maneuverCount(counts, m.code);
                      return (
                        <td
                          key={m.code}
                          className={`lr-col-maneuver lr-cell-num${!r.attended ? " is-na" : n === 0 ? " is-zero" : ""}`}
                          title={r.attended ? `${m.label}: ${n}` : undefined}
                        >
                          {r.attended ? n : "—"}
                        </td>
                      );
                    })}
                    <td className="lr-col-rating">
                      {r.attended && score !== null ? (
                        <LessonReportStarDisplay value={score} />
                      ) : (
                        <span className="lr-na">—</span>
                      )}
                    </td>
                    <td className="lr-col-note">
                      <LessonReportNoteCell note={r.general_notes} />
                    </td>
                    <td className="lr-col-action">
                      <button
                        type="button"
                        className="kp-btn kp-btn--small"
                        onClick={() => setOpening(r)}
                      >
                        Detay
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="lr-table__total">
                <td colSpan={META_COLS} className="lr-table__total-label">
                  Toplam (bu sayfa)
                </td>
                {MANEUVER_ITEMS.map((m) => (
                  <td key={m.code} className="lr-col-maneuver lr-cell-num lr-cell-total">
                    {pageTotals.maneuverSums[m.code]}
                  </td>
                ))}
                <td className="lr-col-rating">
                  {pageTotals.avgScore !== null ? (
                    <LessonReportStarDisplay value={pageTotals.avgScore} />
                  ) : (
                    <span className="lr-na">—</span>
                  )}
                </td>
                <td colSpan={2} className="lr-table__total-meta">
                  {pageTotals.scoredLessons > 0
                    ? `${pageTotals.scoredLessons} ders ortalaması`
                    : ""}
                </td>
              </tr>
            </tfoot>
          </table>
        )}
        <ListPaginationBar
          total={total}
          offset={offset}
          limit={pageSize}
          loading={loading}
          onOffsetChange={setOffset}
        />
      </section>

      {opening && (
        <LessonReportModal
          appointmentId={opening.appointment_id}
          studentName={opening.student_name}
          instructorName={opening.instructor_name}
          vehiclePlate={opening.vehicle_plate ?? undefined}
          startsAt={opening.appointment.starts_at}
          endsAt={opening.appointment.ends_at}
          onClose={() => setOpening(null)}
          onSaved={() => reload()}
        />
      )}
    </div>
  );
}
